import { CUST_REQ_STATUS } from '@indipia/shared-features';
import type { CustReqItem } from '@indipia/shared-features';
import type {
  CustReqOwnerApplyInput,
  CustReqReportItem,
  CustReqReportSummary,
} from '../../types/custReqReport.types';

// 작업보고 화면의 고객 요청(S16) 순수 로직 — 배지 문구·색, 응답 병합, 사장님 직접 작성의 공정 확정·반영 입력 조립.
// 근거: 기획 P_기획발췌 4.2(C-2·C-3·D), 확정결정 16·17·23·25, K1 3.8(E5)·3.9.2.

export const CUST_REQ_REPORT_MESSAGE = {
  noCheck: '반장님 체크 없음',
  noCheckOwner: '체크 없음',
  apply: '반영 처리',
  applying: '처리 중…',
  applied: '반영',
  customerPhoto: '고객 사진',
  confirmKeep: '반영된 요청은 확인 처리되고, 체크 없는 요청은 그대로 남아요.',
  loadFail: '고객 요청을 불러오지 못했습니다.',
} as const;

/** 목록 카드·이력 카드 요약 문구 — '고객 요청 n · 반영 n' */
export function summaryText(summary: CustReqReportSummary): string {
  return `고객 요청 ${summary.requestCount} · 반영 ${summary.appliedCount}`;
}

/** 요약 색 — 모두 반영이면 초록, 아니면 주황 (색과 함께 숫자를 항상 병기) */
export function summaryTone(summary: CustReqReportSummary): 'success' | 'warning' {
  return summary.requestCount > 0 && summary.appliedCount >= summary.requestCount ? 'success' : 'warning';
}

/** 요약 블록을 보일지 — 요청이 1건도 없으면 숨김 */
export function hasCustReq(summary: CustReqReportSummary | undefined | null, count?: number): boolean {
  return (summary?.requestCount ?? count ?? 0) > 0;
}

/** 목록 응답의 요약이 없을 때(배포 시차) 항목에서 직접 센 요약 */
export function countSummary(items: ReadonlyArray<{ applied: boolean }>): CustReqReportSummary {
  return { requestCount: items.length, appliedCount: items.filter((i) => i.applied).length };
}

/** 검수·조치 완료 안내 꼬리 — '고객 요청 n건 확인', 0건이면 빈 문자열 */
export function checkedNote(n: number | undefined | null): string {
  return n != null && n > 0 ? ` · 고객 요청 ${n}건 확인` : '';
}

interface WithCustReq {
  customerRequests?: CustReqReportItem[];
  customerRequestSummary?: CustReqReportSummary;
}

/** [반영 처리] 응답 1건을 보고에 반영 — 요청 목록의 해당 건 교체 후 건수 재계산 */
export function patchApplied<T extends WithCustReq>(report: T, item: CustReqReportItem): T {
  const list = report.customerRequests;
  if (!list?.some((r) => r.custReqId === item.custReqId)) return report;
  const next = list.map((r) => (r.custReqId === item.custReqId ? item : r));
  return { ...report, customerRequests: next, customerRequestSummary: countSummary(next) };
}

/** 사진 순번 오름차순 */
export function sortPhotos<T extends { photoSeq: number }>(photos: readonly T[]): T[] {
  return [...photos].sort((a, b) => a.photoSeq - b.photoSeq);
}

// ── 사장님 직접 작성(R-33) ─────────────────────────────────────────

/**
 * 공정(taskId) 확정 — 공종을 정확히 1개 고르고, 그 공종 코드가 스케줄에 공정 1개로만 있을 때만 확정한다.
 * 공종 마스터에서 직접 고른 공종·스케줄 없는 현장·같은 코드 공정이 여러 개(모호)인 경우는 null(고객 요청 영역 숨김, 확정결정 25).
 */
export function resolveOwnerTaskId(
  selectedCodes: readonly string[],
  scheduleWorkTypes: ReadonlyArray<{ taskId: number; workTypeCode: string }> | undefined | null,
): number | null {
  if (selectedCodes.length !== 1) return null;
  const matches = (scheduleWorkTypes ?? []).filter((w) => w.workTypeCode === selectedCodes[0]);
  return matches.length === 1 ? matches[0].taskId : null;
}

/** 반영 대상 — 그 공정에 전달돼 아직 어느 보고에서도 반영되지 않은 요청(전달됨). 이전 보고 반영분·확인 건은 숨김(확정결정 17) */
export function ownerTargets(items: readonly CustReqItem[]): CustReqItem[] {
  return items.filter((i) => i.statusCode === CUST_REQ_STATUS.DELIVERED);
}

/** 요청별 [반영] 체크 상태 — 키는 custReqId, 값이 없으면 체크하지 않은 것 */
export type OwnerChecks = Readonly<Record<number, boolean | undefined>>;

/** 저장 시 보낼 반영 입력 — 화면에 보인 대상 전부(체크 안 한 건은 applied=false) */
export function buildOwnerApplyInputs(
  targets: readonly Pick<CustReqItem, 'custReqId'>[],
  checks: OwnerChecks,
): CustReqOwnerApplyInput[] {
  return targets.map((t) => ({ custReqId: t.custReqId, applied: checks[t.custReqId] === true }));
}

/** 저장 실패 문구 — 고객 요청을 함께 보냈고 서버가 고객 요청 오류 코드를 주면 서버 문구를 그대로 쓴다 */
export function saveErrorMessage(err: unknown, hadCustReq: boolean, fallback: string): string {
  if (!hadCustReq) return fallback;
  const data = (err as { response?: { data?: { code?: unknown; message?: unknown } } } | null)?.response?.data;
  const code = typeof data?.code === 'string' ? data.code : '';
  const msg = typeof data?.message === 'string' ? data.message.trim() : '';
  return code.startsWith('CUST_REQ_') && msg !== '' ? msg : fallback;
}
